import {
  getAddressesResponse,
  quickAddressesResponse,
  quickAddressResponse,
  savedAddressResponse,
} from '../data/address-response';
import {
  AddressRecipientUpdate,
  AddressToSave,
  AddressUpdate,
  BaseAddress,
  GetAddresses,
  QuickAddressesResponse,
  QuickAddressResponse,
  QuickAddressSubmitData,
  SaveAddressesResponse,
  SaveAddressResponse,
  UpdateAddress,
} from '../global-types/addresses';
import type { ApiResponse } from '../global-types/services';
import { MutationConfig, mutationOptions } from '../utils/react-query';
import { getAccountIdFromCookie } from '../utils/utility/get-account-id-from-cookie';
import { processResponse } from './middleware';
import API from '@dotcom/api-middleware';
import { infiniteQueryOptions } from '@tanstack/react-query';
import axios from 'axios';

const PODService = new API();
const accountId = getAccountIdFromCookie();
const mockResponse = process.env.REACT_APP_MOCK_INITIALIZATION_SERVICE === 'true';
const smartyStreetApiKey = process.env.REACT_APP_SMARTYSTREET_API_KEY;

// The backend rejects display names with leading/trailing whitespace (err_display_name_format_invalid).
const withTrimmedDisplayName = <T extends { display_name?: string }>(address: T): T =>
  typeof address.display_name === 'string' ? { ...address, display_name: address.display_name.trim() } : address;

// Project addresses need this flag for the PDF to render display_name (preview uses its own use_display_name_for_* flags).
const shouldUseDisplayName = (address: { display_name?: string }): boolean => !!address.display_name?.trim();

export const getAddresses = (projectId: string): Promise<GetAddresses> => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      resolve(getAddressesResponse);
    }
    const response = PODService.get('/customization', `/${accountId}/${projectId}/addresses`);
    processResponse(response, resolve, reject);
  });
};

export const saveAddress = (
  projectId: string,
  address: AddressToSave,
  addressVerificationEnhancedMatch: boolean,
): Promise<SaveAddressResponse> => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      resolve(savedAddressResponse);
    }
    const response = PODService.post('/customization', `/${accountId}/${projectId}/addresses`, {
      ...withTrimmedDisplayName(address),
      address_verification_enhanced_match: addressVerificationEnhancedMatch,
      use_display_name: shouldUseDisplayName(address),
    });
    processResponse(response, resolve, reject);
  });
};

export const saveAddresses = (
  projectId: string,
  body: {
    addresses: {
      address: Omit<BaseAddress, 'address_type_code'>;
      is_verified?: boolean;
    }[];
  },
): Promise<SaveAddressesResponse> => {
  return new Promise((resolve, reject) => {
    const response = PODService.post('/customization', `/${accountId}/${projectId}/addresses/list`, {
      ...body,
      addresses: body.addresses.map((entry) => ({
        ...entry,
        address: withTrimmedDisplayName(entry.address),
        use_display_name: shouldUseDisplayName(entry.address),
      })),
    });
    processResponse(response, resolve, reject);
  });
};

export const updateAddress = (
  projectId: string,
  addressId: string,
  address: AddressRecipientUpdate,
  addressVerificationEnhancedMatch: boolean,
): Promise<UpdateAddress> => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      resolve(savedAddressResponse);
    }
    const response = PODService.put('/customization', `/${accountId}/${projectId}/addresses/${addressId}`, {
      ...withTrimmedDisplayName(address),
      address_verification_enhanced_match: addressVerificationEnhancedMatch,
      use_display_name: shouldUseDisplayName(address),
    });
    processResponse(response, resolve, reject);
  });
};

/**
 * Disassociates an address from a project.  For instance, when a user swaps from wanting
 * a return address envelope to a blank envelope.
 *
 * @param projectId - ProjectID associated with the card
 * @param addressId - AddressID associated with the address
 * @returns void
 */
export const deleteAddress = (projectId: string, addressId: string): Promise<ApiResponse<null> | null> => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      resolve(null);
    }
    const response = PODService.delete('/customization', `/${accountId}/${projectId}/addresses/${addressId}`);
    processResponse(response, resolve, reject);
  });
};

/**
 * Disassociates an array of addresses from a project.  For instance, when a user unchecks a checkbox of an address
 * in the address book that was previously associated to the project.
 *
 * @param projectId - ProjectID associated with the card
 * @param addressIds - AddressID associated with the address
 * @returns void
 */
export const deleteAddresses = (projectId: string, addressIds: string[]): Promise<ApiResponse<null> | null> => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      resolve(null);
    }
    const response = PODService.delete('/customization', `/${accountId}/${projectId}?address_ids=${addressIds}`);
    processResponse(response, resolve, reject);
  });
};

export const saveQuickAddress = (address: QuickAddressSubmitData): Promise<QuickAddressResponse> => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      resolve(quickAddressResponse);
    }
    const response = PODService.post('/address-book/', accountId, withTrimmedDisplayName(address));
    processResponse(response, resolve, reject);
  });
};

const saveQuickAddressMutation = (config?: MutationConfig<typeof saveQuickAddress>) =>
  mutationOptions({
    mutationFn: saveQuickAddress,
    config,
  });

export const deleteQuickAddress = (contactId: string): Promise<ApiResponse<null> | null> => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      resolve(null);
    }
    const response = PODService.delete('/address-book', `/${accountId}/${contactId}`);
    processResponse(response, resolve, reject);
  });
};

export const getQuickAddresses = (params?: { page?: string; limit?: string }): Promise<QuickAddressesResponse> => {
  return new Promise((resolve, reject) => {
    const { page = 1, limit = 10 } = params || {};
    if (mockResponse) {
      resolve(quickAddressesResponse);
    }
    const path = `${accountId}?p[page]=[{"eq":"${page}"}]&p[limit]=[{"eq":"${limit}"}]&q[sort_by]=[{"field":"last_updated_at","order":"desc"}]`;
    const response = PODService.get('/address-book/', `${path}`);
    processResponse(response, resolve, reject);
  });
};

export const getNonPaginatedQuickAddresses = (params?: {
  page?: string;
  limit?: string;
}): Promise<QuickAddressesResponse> => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      resolve(quickAddressesResponse);
    }
    const path = `${accountId}?p[page]=[{"eq": "1"}]&p[limit]=[{"eq": "300"}]&q[sort_by]=[{"field":"last_updated_at","order":"desc"}]`;
    const response = PODService.get('/address-book/', `${path}`);
    processResponse(response, resolve, reject);
  });
};

export const DEFAULT_CONTACT_LIMIT = 20;
export const DEFAULT_PAGE = 1;

const getQuickAddressesInfiniteQuery = (options?: {
  onSuccess?: (data: NonNullable<QuickAddressesResponse['data']>) => void;
  onError?: (error: any) => void;
}) =>
  infiniteQueryOptions({
    queryKey: ['addresses'],
    queryFn: ({ pageParam }) =>
      getQuickAddresses({
        page: pageParam.toString(),
        limit: DEFAULT_CONTACT_LIMIT.toString(),
      })
        .then((res) => {
          if (!res.data) {
            throw new Error('No data returned from API');
          }

          options?.onSuccess?.(res.data);

          return res.data;
        })
        .catch((err) => {
          options?.onError?.(err);

          throw err;
        }),
    initialPageParam: DEFAULT_PAGE,
    getNextPageParam: (prev) => prev.paging.next_page,
  });

export const fetchUsStates = async () => {
  const usStatesUrl = process.env.REACT_APP_US_STATES_S3_URL;
  if (usStatesUrl) {
    const response = await fetch(usStatesUrl);
    const usStatesData = await response.json();
    return usStatesData;
  }
};

export const updateQuickAddress = (
  contactId: string,
  addressData: AddressUpdate,
): Promise<QuickAddressResponse | null> => {
  return new Promise((resolve, reject) => {
    if (mockResponse) {
      resolve(null);
    }
    const response = PODService.put('/address-book', `/${accountId}/${contactId}`, withTrimmedDisplayName(addressData));
    processResponse(response, resolve, reject);
  });
};

export const bulkUploadAddresses = <T>(file: File): Promise<ApiResponse<T | null> | null> => {
  return new Promise((resolve, reject) => {
    const formData = new FormData();
    formData.append('file', file);
    const response = PODService.post('/address-book', `/${accountId}/bulk`, formData);
    processResponse(response, resolve, reject);
  });
};

/**
 * Call to smarty street API to retrieve suggested addresses
 * @returns JSON response object containing address suggestions
 */
export const getSuggestedAddresses = (userInput: string) => {
  const baseUrl = process.env.REACT_APP_SMARTYSTREET_URL;
  const callUrl = `${baseUrl}/lookup?key=${smartyStreetApiKey}&search=${userInput}`;
  return axios.get(callUrl);
};

export const addressOptions = {
  getQuickAddressesInfiniteQuery,
  saveQuickAddressMutation,
};
